/**
 * "Browse channels": every public channel (with member counts) plus the private ones I'm in, searchable, filtered
 * (all / joined / not joined / archived), with join and leave. Shows the cache at once (offline too) and adds what
 * only the server's list has (archived channels) when online.
 */
import type { Channel } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { browseChannels, canCreateChannels, type BrowseFilter, type BrowseRow } from "../logic/channels.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type BrowseChannelsState = {
  query: string;
  filter: BrowseFilter;
  rows: BrowseRow[];
  /** The server list is loading. */
  loading: boolean;
  /** Join / leave in flight, by channel id. */
  busy: string[];
  canCreate: boolean;
  notice?: MessageDescriptor;
};

export class BrowseChannelsViewModel extends ViewModel<BrowseChannelsState> {
  private readonly ws: WorkspaceViewModel;
  private listed: Channel[] = [];

  constructor(ws: WorkspaceViewModel) {
    super({ query: "", filter: "all", rows: [], loading: false, busy: [], canCreate: false });
    this.ws = ws;
    this.onDispose(ws.subscribe(() => this.recompute({})));
    this.recompute({});
  }

  private recompute(patch: Partial<BrowseChannelsState>): void {
    const s = { ...this.state, ...patch };
    const cache = this.ws.state.cache;
    if (cache === undefined) {
      this.update(patch);
      return;
    }
    this.update({
      ...patch,
      rows: browseChannels(cache, this.listed, s.query, s.filter),
      canCreate: canCreateChannels(cache, this.ws.deps.clock.now()),
    });
  }

  /** Fetches the server's list (archived channels, fresh member counts); the cache alone works offline. */
  async refresh(): Promise<void> {
    if (!this.ws.state.network) return;
    this.update({ loading: true });
    try {
      this.listed = await this.ws.deps.data.listChannels(this.ws.state.wsId, true);
    } catch {
      // Keep what the cache shows.
    }
    if (!this.isDisposed) this.recompute({ loading: false });
  }

  setQuery(query: string): void {
    this.recompute({ query });
  }

  setFilter(filter: BrowseFilter): void {
    this.recompute({ filter });
  }

  private setBusy(channelId: string, on: boolean): void {
    const busy = this.state.busy.filter((x) => x !== channelId);
    this.update({ busy: on ? [...busy, channelId] : busy });
  }

  /** Joins a public channel (online). Resolves true when joined. */
  async join(channelId: string): Promise<boolean> {
    this.setBusy(channelId, true);
    try {
      await this.ws.deps.data.joinChannel(this.ws.state.wsId, channelId);
      return true;
    } catch {
      this.update({ notice: this.ws.state.network ? msg("channels.join.failed") : msg("channels.join.offline") });
      return false;
    } finally {
      if (!this.isDisposed) this.setBusy(channelId, false);
    }
  }

  leave(channelId: string): void {
    this.ws.deps.data.leaveChannel(this.ws.state.wsId, channelId).catch(() => {
      if (!this.isDisposed) this.update({ notice: msg("channels.leave.failed") });
    });
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
